import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { KeenIcon } from '@/components';
import { useMapboxAccessToken } from '@/hooks/useMapboxAccessToken';
import { Container } from '@/components/container';
import {
  useGetManualAnnotationDetailsQuery,
  useManualAnnotationUpdateMutation,
  useManualAnnotationEditDimensionsMutation,
} from '@/services/RoutingManagementService';
import { useSnackbar } from 'notistack';
import NumberInput from '@/components/inputControls/NumberInput';

const ManualRouteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const fileId = Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { isMapboxReady } = useMapboxAccessToken();

  const {
    data: manualDetails,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetManualAnnotationDetailsQuery({ id: fileId, geojson: true }, {
    skip: Number.isNaN(fileId),
  } as unknown as undefined); // cast because current RTK hook type doesn't expose options generic

  const [manualAnnotationUpdate, { isLoading: isUpdating }] =
    useManualAnnotationUpdateMutation();
  const [manualAnnotationEditDimensions, { isLoading: isUpdatingDimensions }] =
    useManualAnnotationEditDimensionsMutation();

  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(
    null
  );

  const [fairwaysGeoJson, setFairwaysGeoJson] =
    useState<GeoJSON.FeatureCollection | null>(null);

  const getFirstFeatureNumericProp = (keys: string[]) => {
    const feature = geojson?.features?.[0] as
      | GeoJSON.Feature<GeoJSON.Geometry, Record<string, unknown>>
      | undefined;
    const props = feature?.properties;
    if (!props) return undefined;

    for (const key of keys) {
      const v = props[key];
      if (typeof v === 'number' && !Number.isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
      }
    }

    return undefined;
  };

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState<string>('');
  const [webglError, setWebglError] = useState(false);

  const [isEditingDimensions, setIsEditingDimensions] = useState(false);
  const [editHeight, setEditHeight] = useState<string>('');
  const [editDepth, setEditDepth] = useState<string>('');

  useEffect(() => {
    const loadGeojson = async () => {
      if (manualDetails?.geojson_content) {
        // Prefer inline geojson content when provided by API
        setGeojson(manualDetails.geojson_content as GeoJSON.FeatureCollection);
        return;
      }

      // Fallback to fetching via download URL for backward compatibility
      if (!manualDetails?.geojson_download_url) return;
      try {
        const response = await fetch(manualDetails.geojson_download_url);
        if (!response.ok) {
          throw new Error(
            `Failed to load manual route geojson: ${response.status}`
          );
        }
        const data = (await response.json()) as GeoJSON.FeatureCollection;
        setGeojson(data);
      } catch (e) {
        console.error('Error fetching manual route geojson', e);
      }
    };

    loadGeojson();
  }, [manualDetails]);

  // Load fairways from static file (same as ManualRouteModal)
  useEffect(() => {
    let cancelled = false;

    const loadFairways = async () => {
      try {
        const response = await fetch('/data/fairways.geojson');
        if (!response.ok) {
          throw new Error(
            `Failed to load fairways.geojson: ${response.status}`
          );
        }
        const data = await response.json();

        let featureCollection: GeoJSON.FeatureCollection;
        if (
          data &&
          data.type === 'FeatureCollection' &&
          Array.isArray(data.features)
        ) {
          featureCollection = data as GeoJSON.FeatureCollection;
        } else if (Array.isArray(data)) {
          featureCollection = {
            type: 'FeatureCollection',
            features: data as GeoJSON.Feature[],
          };
        } else {
          throw new Error('Unexpected fairways.geojson format');
        }

        if (!cancelled) {
          setFairwaysGeoJson(featureCollection);
        }
      } catch (error) {
        console.error('Error loading fairways:', error);
      }
    };

    loadFairways();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!manualDetails) return;
    if (isEditing) return;

    setEditName(manualDetails.name ?? '');
    setEditDescription(manualDetails.description ?? '');
    setEditTags(manualDetails.tags ?? []);
    setTagInput('');
  }, [manualDetails, isEditing]);

  useEffect(() => {
    if (!mapContainerRef.current || !geojson) return;

    if (mapRef.current) {
      try {
        if (!mapRef.current._removed) {
          mapRef.current.remove();
        }
      } catch (err) {
        console.warn('Error removing map instance:', err);
      }
      mapRef.current = null;
    }

    let center: [number, number] = [24.9384, 60.1699];

    if (geojson.features.length > 0) {
      const firstGeometry = geojson.features[0].geometry;

      const getFirstPosition = (coord: unknown): [number, number] | null => {
        if (Array.isArray(coord) && coord.length > 0) {
          if (typeof coord[0] === 'number' && coord.length >= 2) {
            return [coord[0] as number, coord[1] as number];
          }
          if (Array.isArray(coord[0])) {
            return getFirstPosition(coord[0]);
          }
        }
        return null;
      };

      if (firstGeometry && firstGeometry.type !== 'GeometryCollection') {
        const coords = (
          firstGeometry as
            | GeoJSON.Point
            | GeoJSON.LineString
            | GeoJSON.Polygon
            | GeoJSON.MultiPoint
            | GeoJSON.MultiLineString
            | GeoJSON.MultiPolygon
        ).coordinates;
        const firstPos = getFirstPosition(coords);
        if (firstPos) {
          center = firstPos;
        }
      }
    }

    if (!mapboxgl.supported()) {
      setWebglError(true);
      return;
    }

    let map: mapboxgl.Map;
    try {
      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/hmesikam/cmhkf5ut7001v01sgdjrdhn2b',
        center,
        zoom: 9,
      });

      mapRef.current = map;
    } catch (e) {
      console.error('WebGL Error:', e);
      setWebglError(true);
      return;
    }

    map.on('load', () => {
      if (!mapRef.current || !geojson) return;

      // --- Fairway layer (black lines, same as ManualRouteModal) ---
      if (mapRef.current.getSource('routes-fairways')) {
        mapRef.current.removeLayer('routes-fairways-line');
        mapRef.current.removeSource('routes-fairways');
      }

      if (fairwaysGeoJson) {
        mapRef.current.addSource('routes-fairways', {
          type: 'geojson',
          data: fairwaysGeoJson,
        });

        mapRef.current.addLayer({
          id: 'routes-fairways-line',
          type: 'line',
          source: 'routes-fairways',
          paint: {
            'line-color': '#000000',
            'line-width': 1.5,
            'line-opacity': 0.7,
          },
        });
      }

      // --- Selected route (red lines, rendered on top of fairways) ---
      if (mapRef.current.getSource('manual-route-view')) {
        mapRef.current.removeLayer('manual-route-view-line');
        mapRef.current.removeSource('manual-route-view');
      }

      mapRef.current.addSource('manual-route-view', {
        type: 'geojson',
        data: geojson,
      });

      mapRef.current.addLayer({
        id: 'manual-route-view-line',
        type: 'line',
        source: 'manual-route-view',
        paint: {
          'line-color': '#ff0000',
          'line-width': 3,
          'line-opacity': 0.9,
        },
      });
    });

    return () => {
      if (mapRef.current) {
        try {
          if (!mapRef.current._removed) {
            mapRef.current.remove();
          }
        } catch (err) {
          console.warn('Error removing map instance:', err);
        }
        mapRef.current = null;
      }
    };
  }, [geojson, fairwaysGeoJson, isMapboxReady]);

  const handleBack = () => {
    navigate(-1);
  };

  const isInitialLoading = isLoading || (!geojson && !isError);

  const heightM =
    getFirstFeatureNumericProp(['height', 'height_m', 'min_height']) ??
    (manualDetails?.height
      ? Number(manualDetails.height)
      : manualDetails?.height_m);
  const depthM =
    getFirstFeatureNumericProp(['depth', 'depth_m']) ??
    (manualDetails?.depth
      ? Number(manualDetails.depth)
      : manualDetails?.depth_m);

  useEffect(() => {
    if (!manualDetails) return;
    if (isEditingDimensions) return;

    setEditHeight(typeof heightM === 'number' ? String(heightM) : '');
    setEditDepth(typeof depthM === 'number' ? String(depthM) : '');
  }, [manualDetails, geojson, isEditingDimensions, heightM, depthM]);

  const validateTags = (newTags: string[]) => {
    if (newTags.length > 10) {
      enqueueSnackbar('Maximum 10 tags allowed', { variant: 'error' });
      return false;
    }
    if (newTags.some((t) => t.length > 50)) {
      enqueueSnackbar('Tag cannot exceed 50 characters', { variant: 'error' });
      return false;
    }
    return true;
  };

  const handleTagAdd = () => {
    if (!isEditing) return;
    const candidate = tagInput.trim();
    if (!candidate) return;
    if (candidate.length > 50) {
      enqueueSnackbar('Tag cannot exceed 50 characters', { variant: 'error' });
      return;
    }
    if (editTags.includes(candidate)) {
      setTagInput('');
      return;
    }
    const next = [...editTags, candidate];
    if (!validateTags(next)) return;
    setEditTags(next);
    setTagInput('');
  };

  const handleTagRemove = (tagToRemove: string) => {
    if (!isEditing) return;
    const next = editTags.filter((t) => t !== tagToRemove);
    setEditTags(next);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditName(manualDetails?.name ?? '');
    setEditDescription(manualDetails?.description ?? '');
    setEditTags(manualDetails?.tags ?? []);
    setTagInput('');
  };

  const handleSaveEdit = async () => {
    const nameTrimmed = editName.trim();
    if (!nameTrimmed) {
      enqueueSnackbar('Name is required', { variant: 'error' });
      return;
    }

    try {
      await manualAnnotationUpdate({
        id: fileId,
        payload: {
          name: nameTrimmed,
          description: editDescription.trim(),
          tags: editTags,
        },
      }).unwrap();
      enqueueSnackbar('Manual route updated successfully', {
        variant: 'success',
      });
      setIsEditing(false);
      refetch();
    } catch (e) {
      console.error('Failed to update manual annotation', e);
      enqueueSnackbar('Failed to update manual route', { variant: 'error' });
    }
  };

  const handleCancelDimensions = () => {
    setIsEditingDimensions(false);
    setEditHeight(typeof heightM === 'number' ? String(heightM) : '');
    setEditDepth(typeof depthM === 'number' ? String(depthM) : '');
  };

  const handleSaveDimensions = async () => {
    const formData = new FormData();
    if (editHeight.trim()) {
      formData.append('height', editHeight.trim());
    }
    if (editDepth.trim()) {
      formData.append('depth', editDepth.trim());
    }

    try {
      await manualAnnotationEditDimensions({
        id: fileId,
        payload: formData,
      }).unwrap();
      enqueueSnackbar('Dimensions updated successfully', {
        variant: 'success',
      });
      setIsEditingDimensions(false);
      refetch();
    } catch (e) {
      console.error('Failed to update dimensions', e);
      enqueueSnackbar('Failed to update dimensions', { variant: 'error' });
    }
  };

  return (
    <Container>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Go back to route listings"
            title="Back to Routes"
            type="button"
            disabled={isInitialLoading || isUpdating}
          >
            <KeenIcon icon="arrow-left" className="text-gray-600 text-lg" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold">Manual Route Details</h1>
            {manualDetails?.geojson_filename && (
              <div className="text-xs text-gray-500">
                <span className="font-medium">File:</span>{' '}
                {manualDetails.geojson_filename}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {!isInitialLoading &&
            !isError &&
            geojson &&
            geojson.features.length > 0 && (
              <>
                {!isEditing ? (
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      className="btn btn-light"
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isUpdating}
                    >
                      Cancel Edit
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={isUpdating}
                    >
                      Save
                    </button>
                  </>
                )}
              </>
            )}
          <button
            className="btn btn-light"
            type="button"
            onClick={handleBack}
            disabled={isInitialLoading || isUpdating}
          >
            Cancel
          </button>
        </div>
      </div>

      {isInitialLoading ? (
        <div className="mt-4 pt-2 text-sm text-gray-600 flex items-center gap-2">
          <KeenIcon icon="spinner" className="animate-spin text-lg" />
          <span>Loading manual route...</span>
        </div>
      ) : isError ? (
        <div className="text-sm text-red-600 mb-4" role="alert">
          Error loading manual route: {error?.toString()}
        </div>
      ) : !geojson || geojson.features.length === 0 ? (
        <div className="text-sm text-gray-600 mb-4" role="alert">
          No manual route data found.
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="manual-route-details-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Name
                </label>
                <input
                  id="manual-route-details-name"
                  type="text"
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  value={isEditing ? editName : (manualDetails?.name ?? '')}
                  onChange={(e) => setEditName(e.target.value)}
                  disabled={!isEditing || isUpdating}
                  autoComplete="off"
                />
              </div>
              <div>
                <label
                  htmlFor="manual-route-details-description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Description
                </label>
                <input
                  id="manual-route-details-description"
                  type="text"
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  value={
                    isEditing
                      ? editDescription
                      : (manualDetails?.description ?? '')
                  }
                  onChange={(e) => setEditDescription(e.target.value)}
                  disabled={!isEditing || isUpdating}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="manual-route-details-tags"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Tags
              </label>
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <input
                    id="manual-route-details-tags"
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    disabled={!isEditing || isUpdating}
                    className="input input-bordered w-full h-11 rounded-lg border-gray-300 pr-20"
                    placeholder="Add tag"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleTagAdd();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  disabled={!isEditing || isUpdating || !tagInput.trim()}
                  onClick={handleTagAdd}
                  className="btn btn-sm h-11 btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 disabled:opacity-50"
                >
                  Add Tag
                </button>
              </div>

              {((isEditing ? editTags : manualDetails?.tags) || []).length >
                0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {(isEditing ? editTags : manualDetails?.tags || []).map(
                    (tag) => (
                      <div
                        key={tag}
                        className="bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-100 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        {tag}
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => handleTagRemove(tag)}
                            className="text-blue-800 hover:text-blue-900"
                            aria-label={`Remove ${tag} tag`}
                            disabled={isUpdating}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-8 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Dimensions
              </h2>
              {!isEditingDimensions ? (
                <button
                  className="btn btn-sm btn-light"
                  type="button"
                  onClick={() => setIsEditingDimensions(true)}
                  disabled={isInitialLoading || isUpdatingDimensions}
                >
                  Edit Dimensions
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    className="btn btn-sm btn-light"
                    type="button"
                    onClick={handleCancelDimensions}
                    disabled={isUpdatingDimensions}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    type="button"
                    onClick={handleSaveDimensions}
                    disabled={isUpdatingDimensions}
                  >
                    Save Dimensions
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="manual-route-details-height"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Height (m)
                </label>
                <NumberInput
                  id="manual-route-details-height"
                  data-testid="manual-route-details-height"
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  value={
                    isEditingDimensions
                      ? editHeight
                      : typeof heightM === 'number'
                        ? String(heightM)
                        : ''
                  }
                  onChange={(e) => setEditHeight(e.target.value)}
                  disabled={!isEditingDimensions || isUpdatingDimensions}
                  autoComplete="off"
                  step="0.1"
                />
              </div>
              <div>
                <label
                  htmlFor="manual-route-details-depth"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Depth (m)
                </label>
                <NumberInput
                  id="manual-route-details-depth"
                  data-testid="manual-route-details-depth"
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  value={
                    isEditingDimensions
                      ? editDepth
                      : typeof depthM === 'number'
                        ? String(depthM)
                        : ''
                  }
                  onChange={(e) => setEditDepth(e.target.value)}
                  disabled={!isEditingDimensions || isUpdatingDimensions}
                  autoComplete="off"
                  step="0.1"
                />
              </div>
            </div>

            <div>
              {webglError ? (
                <div className="w-full min-h-[500px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
                  <div className="max-w-md text-center">
                    <div className="flex justify-center mb-4">
                      <KeenIcon
                        icon="shield-cross"
                        className="text-4xl text-danger"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Map Display Not Available
                    </h3>
                    <p className="text-gray-600 mb-6 font-medium">
                      The map cannot be displayed because WebGL (hardware
                      acceleration) is not available in your browser.
                    </p>
                    <div className="bg-gray-200 p-5 rounded-xl border border-gray-200 text-left shadow-sm">
                      <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">
                        How to enable
                      </p>
                      <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                        {`To enable hardware acceleration:

                        • Chrome/Edge: Go to Settings → System → Enable "Use hardware acceleration when available"
                        • Firefox: Go to Settings → General → Performance → Uncheck "Use recommended performance settings" → Check "Use hardware acceleration when available"

                        After making changes, please restart your browser.`}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  ref={mapContainerRef}
                  className="w-full h-[500px] md:h-[600px] rounded-lg overflow-hidden border border-gray-200 shadow-md"
                  aria-label="Map displaying manual route"
                />
              )}
            </div>

            {manualDetails?.notes && (
              <div className="mt-4 text-sm text-gray-700">
                <span className="font-medium">Notes:</span>{' '}
                {manualDetails.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </Container>
  );
};

export default ManualRouteDetails;
